import React, { useContext } from 'react';
import Box from 'Components/Atoms/Box';
import ProductIcon from '../ProductIcon';
import { AuthContext } from 'Contexts/AuthContext';
import './styles.scss';
import { Products } from 'utils/types';
import { noop } from 'utils/helper';
import useRestaurant from 'CustomHooks/useRestaurant';
import { AppId } from 'types/shared/documents/restaurants';
import useManagedWebsites from 'App/Website/useManagedWebsites';
import useShopRetained from 'App/Shop/useShopRetained';
import useTicketDoorApp, { TICKET_DOOR_APP } from 'App/TicketDoor/useTicketDoorApp';
import {
  APP_GATED_IDS,
  EditorProduct,
  MANAGED_WEBSITE_IDS,
  PRODUCTS,
  PRODUCT_IDS,
  SUPPORT_ID,
} from './products';
import { arrangeProducts, readProductLayout } from './productLayout';
import { saveDashboardEdits, SaveDashboardEdits } from './saveDashboardEdits';
import { currentHolders, securityPlusUsable } from './securityRights';
import ProductFolder from './ProductFolder';
import ProductLayoutEditor, { DashboardBanner } from './ProductLayoutEditor';

export type ProductIconsProps = {
  products?: Products[];
  takeAwayNotifications?: number;
  restaurantId?: null | string;
  editorOpen?: boolean;
  onEditorClose?: () => void;
  saveEdits?: SaveDashboardEdits;
  banners?: DashboardBanner[];
  editorDoneButtonContainer?: HTMLElement | null;
};

const ProductIcons = ({
  products = [],
  takeAwayNotifications = 0,
  restaurantId = null,
  editorOpen = false,
  onEditorClose = noop,
  saveEdits = saveDashboardEdits,
  banners = [],
  editorDoneButtonContainer,
}: ProductIconsProps) => {
  const { uid } = useContext(AuthContext);

  const {
    apps,
    hasSiaV02,
    siaState,
    setSupportOpen,
    setnotificationsV03Open,
    dashboardProductLayout,
    securityPlus,
  } = useRestaurant();
  const hasSiaEmbed = apps.includes('sia-phone-assistant' as AppId);
  const siaBadgeCount = hasSiaV02
    ? (siaState.data?.calls_today ?? 0) + (siaState.data?.open_todos ?? 0)
    : 0;

  const { roleMap, isGastronautAdmin } = useContext(AuthContext);
  const canManageRestaurant = !!roleMap?.admin || !!isGastronautAdmin;
  const managedWebsites = useManagedWebsites(restaurantId, canManageRestaurant);
  const protectionActive = securityPlusUsable(apps, securityPlus);
  // After a deactivation the Shop tile keeps leading to the existing orders.
  const shopRetained = useShopRetained(restaurantId, apps.includes('shop' as AppId)) === true;
  // Read for exactly this restaurant (live; never the previous venue's apps while switching).
  const ticketDoorActive = useTicketDoorApp(restaurantId) === 'active';
  const appActive = (app: AppId) =>
    app === TICKET_DOOR_APP ? ticketDoorActive : apps.includes(app);

  const support: EditorProduct = {
    id: SUPPORT_ID,
    product: '',
    label: 'Support',
    onClick: () => {
      setnotificationsV03Open(false);
      setSupportOpen(true);
    },
    locked: false,
    hide: false,
  };
  const catalog: EditorProduct[] = [
    ...PRODUCTS.filter(
      (p) =>
        p.id !== 'ticketDoor' &&
        !(
          (products.includes('light-reservation') && p.id === 'reservations') ||
          (products.includes('reservation') && p.id === 'light-reservations')
        ),
    ).map((p) => ({
      ...p,
      // Shows that the product is PIN-protected, also to admins exempt from
      // the PIN; whether this user must enter it stays with authenticate.
      // Like usePinAuth, it goes by the active published document alone.
      locked:
        !!p.authorize &&
        securityPlus?.active === true &&
        !!currentHolders(securityPlus.settings, p.authorize).length,
      hide:
        // A managed website is activated by Gastronaut, independent of booked products.
        (!p.managedWebsite && !products.some((x) => p.product.includes(x))) ||
        !(!p.app || appActive(p.app as AppId) || (p.app === 'shop' && shopRetained)) ||
        (!!p.adminOnly && !canManageRestaurant) ||
        (!!p.managedWebsite && managedWebsites.status !== 'available') ||
        (hasSiaEmbed && p.id === 'phone' && p.app === 'sia'),
    })),
    support,
  ];
  const productById = new Map(catalog.map((p) => [p.id, p]));
  const candidates = catalog.map((p) => ({
    id: p.id,
    available: !p.hide,
    showWhenUnavailable: !p.dontShowOnHide,
    last: p.id === SUPPORT_ID,
    hiddenUntilAdded: !!p.hiddenUntilAdded,
  }));
  const savedLayout = readProductLayout(dashboardProductLayout?.raw);
  const arrangement = arrangeProducts(candidates, savedLayout);

  const renderProduct = (id: string, onActivated?: () => void) => {
    const product = productById.get(id)!;
    return (
      <ProductIcon
        key={product.id}
        {...product}
        notifications={product.id === 'phone' ? siaBadgeCount : undefined}
        restaurantId={restaurantId}
        onActivated={onActivated}
      />
    );
  };

  if (editorOpen && dashboardProductLayout.loaded && !!restaurantId) {
    return (
      <ProductLayoutEditor
        key={restaurantId}
        restaurantId={restaurantId}
        products={catalog}
        candidates={candidates}
        arrangement={arrangement}
        savedShown={savedLayout?.shown ?? []}
        savedOrder={savedLayout?.order ?? []}
        savedHiddenBanners={savedLayout?.hiddenBanners ?? []}
        banners={banners}
        doneButtonContainer={editorDoneButtonContainer}
        keepPlaceOf={[...MANAGED_WEBSITE_IDS, ...APP_GATED_IDS]}
        securityPlus={securityPlus}
        securityPlusUsable={protectionActive}
        onSave={({ layout, rights }) =>
          saveEdits({
            restaurantId,
            uid: uid ?? null,
            layout: layout ? {
              ...layout,
              hidden: [...new Set([...layout.hidden, ...(savedLayout?.hidden ?? []).filter((id) => id === 'ticketDoor')])],
              shown: [...new Set([...(layout.shown ?? []), ...(savedLayout?.shown ?? []).filter((id) => id === 'ticketDoor')])],
            } : null,
            knownIds: PRODUCT_IDS,
            rights,
          })
        }
        onClose={onEditorClose}
      />
    );
  }

  // Group dashboards are reached from the restaurant selector.
  return (
    <>
      <Box className="product-icons">
        {arrangement.grid.map((id) => renderProduct(id))}
      </Box>
      {!!arrangement.folder.length && (
        <ProductFolder
          productIds={arrangement.folder}
          renderProduct={renderProduct}
        />
      )}
    </>
  );
};

export default ProductIcons;
